import JSZip from 'jszip';
import moment, { Moment } from 'moment';
import { NAlert, NCheckbox, NDatePicker, NForm, NFormItem, NProgress, NSelect, NSpin } from 'naive-ui';
import { defineComponent, PropType } from 'vue';
import * as XLSX from 'xlsx';

import { Station } from '~/api/models/station.model';
import ModalBase from '~/components/modals/modal-base';
import { delay } from '~/misc/time';
import { ExportMeasures, ExportMeasuresValue } from './export-message';
import { Measure } from '~/api/models/measure.model';


enum FileTypeEnum {
    JSON = 'json',
    CSV = 'csv',
    EXCEL = 'xlsx',
}

export default defineComponent({
    extends: ModalBase,

    components: {
        NDatePicker,
        NSpin,
        NProgress,
        NAlert,
        NForm,
        NFormItem,
        NSelect,
        NCheckbox
    },

    props: {
        since: { type: Object as PropType<Moment> },
        before: { type: Object as PropType<Moment> },
        stations: { type: Array as PropType<Station[] | null>, required: true },
    },

    computed: {

        names(): string {
            return this.stations ? this.stations.map(s => this.getStationName(s)).join(', ') : '?';
        },

        stationOptions(): { label: string, value: string }[] {
            return this.stations ? this.stations.map(s => ({ label: this.getStationName(s), value: s.id })) : [];
        },
    },

    data() {
        const start = this.since ? this.since.unix() * 1000 : moment().subtract(1, 'w').unix() * 1000;
        const end = this.before ? this.before.unix() * 1000 : moment().unix() * 1000;

        const fileTypeOptions: { label: string, value: string }[] = [
            {
                label: this.$t('export_data.file_type.csv'),
                value: FileTypeEnum.CSV,
            },
            {
                label: this.$t('export_data.file_type.excel'),
                value: FileTypeEnum.EXCEL,
            },
        ]

        return {
            fileTypes: [FileTypeEnum.JSON, FileTypeEnum.CSV],
            selectedFileType: FileTypeEnum.CSV,
            loading: false,
            percentage: 0,
            fileTypeOptions,
            error: null,
            singleFile: false,
            exportDataForm: {
                range: [start, end],
                stations: [],
                filetype: fileTypeOptions[1].value,
            },

            status: 'default' as 'error' | 'success' | 'default'
        }
    },

    methods: {

        open(): void {
            try {

                this.error = null;
                const start = this.since ? this.since.unix() * 1000 : moment().subtract(1, 'w').unix() * 1000;
                const end = this.before ? this.before.unix() * 1000 : moment().unix() * 1000;

                this.exportDataForm = {
                    range: [start, end],
                    stations: this.stations.map(s => s.id) || [],
                    filetype: this.fileTypeOptions[1].value,
                }

                // @ts-ignore
                this.$refs.modal?.open();
            } catch (error) {
                this.error = this.$t('global.failure')
            }
        },

        async generateData(): Promise<ExportMeasures[]> {

            if (!this.exportDataForm.stations || !this.exportDataForm.stations.length) {
                return [];
            }

            const since: Moment = moment.unix(Math.floor(this.exportDataForm.range[0]) / 1000);
            const before: Moment = moment.unix(Math.floor(this.exportDataForm.range[1]) / 1000);

            let messageToExport: ExportMeasures[] = [];
            let index = 0;

            for (const stationId of this.exportDataForm.stations) {
                const station: Station = this.stations.find(s => s.id === stationId);

                if (!station) {
                    console.warn('station not found', stationId);
                    continue;
                }

                const messages = await this.$stationService.getMeasures(stationId, since, before);
                messageToExport = messageToExport.concat(
                    messages.map((measure: Measure) => {

                        const values = measure?.values;
                        const rawValues = measure?.rawValues;

                        const data = {
                            station: station,
                            date: measure.date,
                            values: {
                                "date": {
                                    column: 'Date',
                                    value: moment.unix(measure.date).format(),
                                },
                                "type": {
                                    column: 'type',
                                    value: measure?.values?.type,
                                },
                                "timestamp": {
                                    column: 'Timestamp',
                                    value: measure.date,
                                },
                                "station": {
                                    column: 'Name',
                                    value: station.name,
                                },
                                "lel": {
                                    column: 'CH4 LEL (%)',
                                    value: values?.CH4_lel >= 3 ? values?.CH4_lel : 0,
                                },
                                "temperature": {
                                    column: 'Temperature (°C)',
                                    value: values?.temperature,
                                },
                                "humidity": {
                                    column: 'Humidity (%RH)',
                                    value: values?.humidity,
                                },
                                "battery": {
                                    column: 'Battery (mV)',
                                    value: values?.battery,
                                },
                                "run_time": {
                                    column: 'Run time (h)',
                                    value: values?.run_time,
                                },

                                "CH4_concentration_alarm": {
                                    column: "CH4 concentration alarm",
                                    value: values?.CH4_concentration_alarm,
                                },

                                "CH4_system_failure_alarm": {
                                    column: "System failure alarm",
                                    value: values?.CH4_sensor_error_alarm || values?.CH4_ndir_fatal_alarm || values?.CH4_ndir_intermittent_alarm || values?.CH4_ndir_communication_alarm || values?.CH4_env_out_of_range_alarm || values?.CH4_internal_alarm,
                                },

                                "CH4_battery_alarm": {
                                    column: "Low battery alarm",
                                    value: values?.CH4_battery_alarm,
                                },

                                "CH4_eol": {
                                    column: "End of life alarm",
                                    value: values?.CH4_eol_alarm,
                                },
                            }
                        } as ExportMeasures;

                        if (this.isLTEMStation(station)) {
                            data.values['rsrq'] = { column: 'RSRQ (dB)', value: values.rsrq };
                            data.values['rssi'] = { column: 'RSSI (dBm)', value: values.rssi };
                            data.values['rsrp'] = { column: 'RSRP (dBm)', value: values.rsrp };
                        }

                        if (this.isLoRaWanStation(station)) {
                            data.values['devEui'] = { column: 'DEV EUI', value: station?.eid };
                            data.values['rssi'] = { column: 'RSSI (dBm)', value: rawValues.rssi };
                            data.values['snr'] = { column: 'SNR (dBm)', value: rawValues?.snr };
                            data.values['spreading_factor'] = { column: 'SF', value: rawValues?.spreading_factor };
                        }

                        return data;
                    })
                );

                this.percentage = Math.ceil(index / this.exportDataForm.stations.length) * 100;
                index++;
            }

            return messageToExport.sort((m1, m2) => m2.date - m1.date);

        },

        getFilename(station: Station, type: 'events' | 'messages' | 'measures', fileType: FileTypeEnum) {

            const start = moment.unix(Math.floor(this.exportDataForm.range[0]) / 1000);
            const end = moment.unix(Math.floor(this.exportDataForm.range[1]) / 1000);

            return `${type}-${station ? station.name?.toLowerCase() : 'all'}_${start.format('YYYY-MM-DD')}_${end.format('YYYY-MM-DD')}.${fileType}`;
        },

        async submit(): Promise<void> {

            this.loading = true;
            this.error = false;

            const messages: ExportMeasures[] = await this.generateData();

            const columns: Set<string> = new Set();
            const values: { [stationId: string]: (string | number)[][] } = { default: [] };

            messages.forEach((m: ExportMeasures) => m.values ? Object.values(m.values).forEach((v: ExportMeasuresValue) => columns.add(v.column)) : null);


            messages.forEach((m: ExportMeasures) => {
                const row = [];
                columns.forEach(column => {
                    const value = Object.values(m.values).find(v => v.column === column)?.value;

                    if (value !== undefined) {
                        row.push(value);
                    } else {
                        row.push('');
                    }
                });

                values[m.station.id] = values[m.station.id] || [];
                values[m.station.id].push(row);
            });

            if (this.singleFile) {
                this.generateInSingleFile(Array.from(columns), values);
            } else {
                this.generateInMultipleFile(Array.from(columns), values);
            }

            this.loading = false;
            this.close();
        },

        async generateInMultipleFile(columns: string[], values: { [stationId: string]: (string | number)[][] }): Promise<void> {
            const filetype = this.exportDataForm.filetype;

            const zip = new JSZip();

            Object.keys(values).forEach((stationId: string) => {
                let content = null;

                switch (filetype) {
                    case FileTypeEnum.CSV:
                        content = this.createCsvContent(columns, values[stationId]);
                        break;
                    case FileTypeEnum.EXCEL:
                        content = this.generateXlsx(columns, values[stationId]);
                        break;
                    default:
                        throw new Error('Unkown file type ' + filetype)
                }

                const file = content;
                const station: Station = this.stations.find(s => s.id === stationId);
                zip.file(this.getFilename(station, 'measures', filetype), file);
            });

            const hiddenElement = document.createElement('a');
            hiddenElement.target = '_blank';

            const since: Moment = moment.unix(Math.floor(this.exportDataForm.range[0]) / 1000);
            const before: Moment = moment.unix(Math.floor(this.exportDataForm.range[1]) / 1000);

            //provide the name for the CSV file to be downloaded  
            const blob = await zip.generateAsync({ type: 'blob' });
            hiddenElement.href = URL.createObjectURL(blob);
            hiddenElement.download = `export_${since.format('YYYY-MM-DD')}_${before.format('YYYY-MM-DD')}`;
            hiddenElement.click();

            await delay(2000);

            hiddenElement.remove();
        },

        async generateInSingleFile(columns: string[], values: { [stationId: string]: (string | number)[][] }): Promise<void> {
            const filetype = this.exportDataForm.filetype;

            const zip = new JSZip();

            const allValues: (string | number)[][] = Object.values(values).reduce((acc: (string | number)[][], actual: (string | number)[][]) => acc.concat(actual), []);
            allValues.sort((row1, row2) => <number>row2[1] - <number>row1[1]);

            let content = null;

            switch (filetype) {
                case FileTypeEnum.CSV:
                    content = this.createCsvContent(columns, allValues);
                    break;
                case FileTypeEnum.EXCEL:
                    content = this.generateXlsx(columns, allValues);
                    break;
                default:
                    throw new Error('Unkown file type ' + filetype)
            }

            const file = content;

            zip.file(this.getFilename(null, 'messages', filetype), file);

            const hiddenElement = document.createElement('a');
            hiddenElement.target = '_blank';

            const since: Moment = moment.unix(Math.floor(this.exportDataForm.range[0]) / 1000);
            const before: Moment = moment.unix(Math.floor(this.exportDataForm.range[1]) / 1000);

            //provide the name for the CSV file to be downloaded  
            const blob = await zip.generateAsync({ type: 'blob' });
            hiddenElement.href = URL.createObjectURL(blob);
            hiddenElement.download = `export_${since.format('YYYY-MM-DD')}_${before.format('YYYY-MM-DD')}`;
            hiddenElement.click();

            await delay(2000);

            hiddenElement.remove();
        },

        generateXlsx(columns: string[], values: (string | number)[][]): Blob {

            values.unshift(columns)

            const workSheet = XLSX.utils.aoa_to_sheet(values, { dateNF: 'DD/MM/YYYY HH:mm:ss' });
            // Generate a Work Book containing the above sheet.
            const workBook = {
                Sheets: { data: workSheet, cols: [] },
                SheetNames: ["data"],
            };
            // Exporting the file with the desired name and extension.
            const excelBuffer = XLSX.write(workBook, { bookType: "xlsx", type: "array" });
            const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
            return new Blob([excelBuffer], { type: fileType });
        },

        createCsvContent(columns: string[], values: (string | number)[][]): string {
            let content = columns.join(',') + '\r\n';
            values.forEach(v => content += Object.values(v).join(',') + '\r\n');
            return content;
        },
    },
});